import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { FileCheck, Eye, FileText, Download, Search, CheckCircle, Clock, AlertTriangle, StopCircle, PlayCircle, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReportPreviewDialog from '@/components/reports/ReportPreviewDialog';

const CHECK_NAMES = {
  'address_check': 'Address Verification',
  'criminal_court_check': 'Criminal Court Check',
  'court_advocate_check': 'Court Advocate Check',
  'id_check': 'ID Verification',
  'employment_check': 'Employment Verification',
  'education_check': 'Education Verification',
  'credit_check': 'Credit Check',
  'drug_test': 'Drug Test',
  'reference_check': 'Reference Check',
  'personal_reference': 'Personal Reference',
  'professional_reference': 'Professional Reference',
  'global_database': 'Global Database Check',
  'world_criminal_db': 'World Criminal Database',
  'india_criminal_db': 'India Criminal Database'
};

const STATUS_CONFIG = {
  'initiated': { label: 'Initiated', bg: 'bg-slate-100 text-slate-700' },
  'not_initiated': { label: 'Not Initiated', bg: 'bg-gray-100 text-gray-600' },
  'details_filled': { label: 'Details Filled', bg: 'bg-cyan-100 text-cyan-700' },
  'pending_submission': { label: 'Pending Submission', bg: 'bg-slate-100 text-slate-700' },
  'pending_client_approval': { label: 'Pending Client Approval', bg: 'bg-indigo-100 text-indigo-700' },
  'pending_qc': { label: 'Pending QC', bg: 'bg-amber-100 text-amber-700' },
  'basic_initiated': { label: 'Basic Initiated', bg: 'bg-orange-100 text-orange-700' },
  'pending_ops_assignment': { label: 'Awaiting Assignment', bg: 'bg-orange-100 text-orange-700' },
  'approved': { label: 'Approved', bg: 'bg-blue-100 text-blue-700' },
  'qc_approved': { label: 'QC Approved', bg: 'bg-blue-100 text-blue-700' },
  'in_progress': { label: 'In Progress', bg: 'bg-blue-100 text-blue-700' },
  'insufficiency': { label: 'Insufficiency', bg: 'bg-amber-100 text-amber-700' },
  'completed': { label: 'Completed', bg: 'bg-emerald-100 text-emerald-700' },
  'final_report_qc': { label: 'Final Report QC', bg: 'bg-purple-100 text-purple-700' },
  'report_approved': { label: 'Report Approved', bg: 'bg-green-100 text-green-700' },
  'on_hold': { label: 'On Hold', bg: 'bg-rose-100 text-rose-700' },
  'stopped': { label: 'Stopped', bg: 'bg-red-100 text-red-700' }
};

// Function to get display status based on actual submission state and check initiation
const getDisplayStatus = (caseItem) => {
  if (!caseItem) return 'initiated';
  
  // If case is in_progress or qc_approved, check if checks have been started
  if (['in_progress', 'qc_approved'].includes(caseItem.status)) {
    const checks = caseItem.checks || [];
    const hasAnyCheckStarted = checks.some(check => 
      check.assigned_at || check.status === 'in_progress' || check.status === 'vendor_assigned' || 
      check.status === 'verified' || check.status === 'completed' ||
      check.verification_progress === 'initiated' || check.verification_progress === 'in_progress'
    );
    if (!hasAnyCheckStarted && checks.length > 0) {
      return 'not_initiated';
    }
    if (caseItem.status === 'qc_approved' && hasAnyCheckStarted) {
      return 'in_progress';
    }
  }
  
  return caseItem.status;
};

const RESULT_CONFIG = {
  'pending': { label: 'Pending', bg: 'bg-slate-100 text-slate-700' },
  'clear': { label: 'Clear', bg: 'bg-emerald-100 text-emerald-700' },
  'green': { label: 'Clear', bg: 'bg-emerald-100 text-emerald-700' },
  'discrepancy': { label: 'Discrepancy', bg: 'bg-rose-100 text-rose-700' },
  'minor_discrepancy': { label: 'Minor Discrepancy', bg: 'bg-orange-100 text-orange-700' },
  'red': { label: 'Discrepancy', bg: 'bg-rose-100 text-rose-700' },
  'insufficiency': { label: 'Insufficiency', bg: 'bg-amber-100 text-amber-700' },
  'unable_to_verify': { label: 'Unable to Verify', bg: 'bg-yellow-100 text-yellow-700' },
  'amber': { label: 'Unable to Verify', bg: 'bg-yellow-100 text-yellow-700' }
};

export default function ClientCasesTab() {
  const [cases, setCases] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewCase, setViewCase] = useState(null);
  const [viewEmployee, setViewEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('latest');
  
  // Drill-down modal state
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [drillDownTitle, setDrillDownTitle] = useState('');
  const [drillDownData, setDrillDownData] = useState([]);
  
  // Approval confirmation dialog state
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approvalCase, setApprovalCase] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [sendingReport, setSendingReport] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [casesRes, employeesRes] = await Promise.all([
        axios.get('/cases'),
        axios.get('/employees')
      ]);
      setCases(casesRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Open approval confirmation dialog
  const openApproveDialog = (caseItem) => {
    setApprovalCase(caseItem);
    setApprovalNotes('');
    setShowApproveDialog(true);
  };

  // Approve case for processing (when pending_client_approval)
  const handleApproveCase = async () => {
    if (!approvalCase) return;
    try {
      await axios.post(`/client/cases/${approvalCase.id}/approve`, {
        notes: approvalNotes || 'Approved for BGV processing'
      });
      toast.success(`Case ${approvalCase.id} approved for processing`);
      setShowApproveDialog(false);
      setApprovalCase(null);
      setApprovalNotes('');
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Approve error:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve case');
    }
  };

  // Stop case verification
  const handleStopCase = async (caseItem) => {
    if (!window.confirm(`Are you sure you want to stop verification for case ${caseItem.id}?`)) {
      return;
    }
    try {
      await axios.post(`/cases/${caseItem.id}/stop`, {
        reason: 'Stopped by HR Admin'
      });
      toast.success(`Case ${caseItem.id} verification stopped`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Stop error:', error);
      toast.error(error.response?.data?.detail || 'Failed to stop case');
    }
  };

  const getEmployee = (employeeId) => employees.find(e => e.id === employeeId);

  const getFormProgress = (caseItem) => {
    const fp = caseItem.form_progress;
    if (!fp) return { completed: 0, total: 6, percentage: 0 };
    return { completed: fp.completed, total: fp.total, percentage: fp.percentage };
  };

  const getVerificationStatus = (checks) => {
    if (!checks || checks.length === 0) return { completed: 0, total: 0, percentage: 0 };
    const completed = checks.filter(c => ['verified', 'completed', 'clear', 'discrepancy', 'unable_to_verify', 'failed'].includes(c.status)).length;
    return { completed, total: checks.length, percentage: Math.round((completed / checks.length) * 100) };
  };

  const getOverallResult = (caseItem, checks) => {
    if (!checks || checks.length === 0) return 'pending';

    // Include NA-by-declaration / not_applicable / stop_check as terminal
    // "completed" statuses so a case doesn't stay Pending forever when a
    // candidate opts out of a check via declaration (Feb 28, 2026 fix).
    const TERMINAL_STATUSES = ['verified', 'completed', 'clear', 'discrepancy', 'unable_to_verify', 'failed', 'not_applicable', 'not_applicable_by_declaration', 'stop_check'];
    const completedChecks = checks.filter(c => TERMINAL_STATUSES.includes(c.status));
    if (completedChecks.length === 0) return 'pending';
    
    const getResult = (c) => c.verification_result || c.result;
    
    const hasDiscrepancy = completedChecks.some(c => 
      ['discrepancy', 'major_discrepancy', 'failed', 'red'].includes(getResult(c)) || 
      c.status === 'discrepancy' || c.status === 'failed'
    );
    const hasUnableToVerify = completedChecks.some(c => 
      c.status === 'unable_to_verify' || getResult(c) === 'unable_to_verify'
    );
    const anyInsuff = checks.some(c => c.has_insufficiency);
    
    // Negative results show immediately regardless of completion
    if (hasDiscrepancy) return 'discrepancy';
    if (hasUnableToVerify) return 'amber';
    if (anyInsuff) return 'insufficiency';
    
    // Only show clear when ALL checks are completed
    const allCompleted = completedChecks.length === checks.length;
    if (allCompleted && !hasDiscrepancy && !hasUnableToVerify) return 'clear';
    
    return 'pending';
  };

  const openViewCase = async (c) => {
    setViewCase(c);
    const emp = getEmployee(c.employee_id);
    if (emp) {
      try {
        const r = await axios.get('/employees/' + emp.id);
        setViewEmployee(r.data);
      } catch (e) {
        setViewEmployee(emp);
      }
    }
  };

  const generateReport = async (caseItem, reportType) => {
    try {
      toast.info(`Generating ${reportType} report...`);
      const response = await axios.post(`/cases/${caseItem.id}/generate-bgv-report`, {
        is_final: reportType === 'final'
      }, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const match = disposition?.match(/filename="?([^"]+)"?/);
      const prefix = reportType === 'final' ? 'FINAL' : 'INTERIM';
      link.download = match ? match[1] : `${prefix}_REPORT_${caseItem.id}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    }
  };

  const handleSendReportEmail = async (caseItem) => {
    setSendingReport(caseItem.id);
    try {
      const res = await axios.post(`/cases/${caseItem.id}/send-report-email`);
      toast.success(`${res.data.report_type} report sent to ${res.data.to_email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send report email');
    } finally {
      setSendingReport(null);
    }
  };

  const filteredCases = cases.filter(c => {
    const employee = getEmployee(c.employee_id);
    const matchesSearch = searchTerm === '' || 
      c.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const displayStatus = getDisplayStatus(c);
    const matchesStatus = statusFilter === 'all' || displayStatus === statusFilter;
    
    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'all' && c.created_at) {
      const caseDate = new Date(c.created_at);
      const now = new Date();
      const daysDiff = Math.floor((now - caseDate) / (1000 * 60 * 60 * 24));
      
      switch (dateFilter) {
        case 'today':
          matchesDate = daysDiff === 0;
          break;
        case 'yesterday':
          matchesDate = daysDiff === 1;
          break;
        case 'last_7_days':
          matchesDate = daysDiff < 7;
          break;
        case 'last_30_days':
          matchesDate = daysDiff < 30;
          break;
        default:
          matchesDate = true;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  }).sort((a, b) => {
    // Sort by created_at date
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return sortOrder === 'latest' ? dateB - dateA : dateA - dateB;
  });

  // Calculate summary stats
  // In Progress: Cases that are actively being processed (employee has submitted and not pending approval)
  const hasInsufficiency = (c) => {
    return c.has_insufficiency || c.has_open_insufficiency || c.checks?.some(ch => ch.has_insufficiency);
  };
  
  const isInProgress = (c) => {
    if (hasInsufficiency(c)) return false; // Exclude insufficiency cases
    if (c.status === 'pending_client_approval') return false;
    if (c.status === 'pending_qc') return false; // Exclude pending QC - QC hasn't approved yet
    if (c.status === 'initiated' && !c.employee_submitted) return false; // Draft/Invited - not in progress
    // Only qc_approved and in_progress count as "In Progress"
    return ['qc_approved', 'in_progress'].includes(c.status);
  };

  const stats = {
    total: cases.length,
    inProgress: cases.filter(isInProgress).length,
    completed: cases.filter(c => c.status === 'completed').length,
    withInsuff: cases.filter(hasInsufficiency).length,
    pendingApproval: cases.filter(c => c.status === 'pending_client_approval').length
  };

  // Drill-down helper functions
  const getInProgressCases = () => cases.filter(isInProgress);
  const getCompletedCases = () => cases.filter(c => c.status === 'completed');
  const getInsufficiencyCases = () => cases.filter(hasInsufficiency);
  const getPendingApprovalCases = () => cases.filter(c => c.status === 'pending_client_approval');

  const openDrillDown = (title, data) => {
    setDrillDownTitle(title);
    // Sort data by created_at date, latest first
    const sortedData = [...data].sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA; // Latest first
    });
    setDrillDownData(sortedData);
    setShowDrillDown(true);
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Verification Cases</h1>
        <p className="text-slate-600">Track verification progress for your employees</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('All Cases', cases)}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Cases</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <FileCheck className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('In Progress Cases', getInProgressCases())}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">In Progress</p>
                <p className="text-2xl font-bold text-blue-700">{stats.inProgress}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('Completed Cases', getCompletedCases())}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600">Completed</p>
                <p className="text-2xl font-bold text-emerald-700">{stats.completed}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('Cases with Insufficiency', getInsufficiencyCases())}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">With Insufficiency</p>
                <p className="text-2xl font-bold text-amber-700">{stats.withInsuff}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by Case ID or Employee..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="initiated">Initiated</SelectItem>
                <SelectItem value="not_initiated">Not Initiated</SelectItem>
                <SelectItem value="pending_submission">Pending Submission</SelectItem>
                <SelectItem value="pending_qc">Pending QC</SelectItem>
                <SelectItem value="qc_approved">QC Approved</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="final_report_qc">Final Report QC</SelectItem>
                <SelectItem value="report_approved">Report Approved</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="stopped">Stopped</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-[180px]" data-testid="date-filter">
                <SelectValue placeholder="Submitted Date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                <SelectItem value="last_30_days">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-[150px]" data-testid="sort-order">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="latest">Latest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Cases Table */}
      {filteredCases.length > 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Case ID</TableHead>
                  <TableHead className="font-semibold">Request Raised</TableHead>
                  <TableHead className="font-semibold">Submitted</TableHead>
                  <TableHead className="font-semibold">Employee</TableHead>
                  <TableHead className="font-semibold">Form Progress</TableHead>
                  <TableHead className="font-semibold">Overall Status</TableHead>
                  <TableHead className="font-semibold">Overall Result</TableHead>
                  <TableHead className="font-semibold text-center">Reports</TableHead>
                  <TableHead className="font-semibold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCases.map((caseItem) => {
                  const employee = getEmployee(caseItem.employee_id);
                  const formProgress = getFormProgress(caseItem);
                  const overallResult = getOverallResult(caseItem, caseItem.checks);
                  const displayStatus = getDisplayStatus(caseItem);
                  const statusConfig = STATUS_CONFIG[displayStatus] || STATUS_CONFIG['initiated'];
                  const resultConfig = RESULT_CONFIG[overallResult] || RESULT_CONFIG['pending'];
                  
                  return (
                    <TableRow key={caseItem.id} className="hover:bg-slate-50">
                      <TableCell className="font-mono text-sm font-medium">
                        {caseItem.id}
                      </TableCell>
                      <TableCell className="text-sm">
                        {(caseItem.invitation_sent_at || caseItem.created_at) ? new Date(caseItem.invitation_sent_at || caseItem.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {caseItem.submitted_at
                          ? new Date(caseItem.submitted_at).toLocaleDateString()
                          : ((caseItem.employee_submitted || caseItem.source === 'bulk_import' || caseItem.source === 'data_entry')
                              ? (caseItem.created_at ? new Date(caseItem.created_at).toLocaleDateString() : '-')
                              : <span className="text-slate-400">Pending</span>)
                        }
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{employee?.name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500">{employee?.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${formProgress.percentage === 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                              style={{ width: `${formProgress.percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-slate-700">{formProgress.completed}/{formProgress.total}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.bg}>{statusConfig.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={resultConfig.bg}>{resultConfig.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openViewCase(caseItem)}
                            title="View Details"
                            data-testid={`view-case-${caseItem.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <ReportPreviewDialog 
                            caseId={caseItem.id} 
                            caseStatus={caseItem.status}
                            trigger={
                              <Button variant="ghost" size="sm" title="Preview Report" className="text-emerald-600" data-testid={`preview-report-${caseItem.id}`}>
                                <FileCheck className="w-4 h-4" />
                              </Button>
                            }
                          />
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleSendReportEmail(caseItem)}
                            title="Send Report Email"
                            disabled={sendingReport === caseItem.id}
                            data-testid={`send-report-${caseItem.id}`}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          >
                            {sendingReport === caseItem.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          {caseItem.status === 'pending_client_approval' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openApproveDialog(caseItem)}
                              title="Approve for Processing"
                              className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                              data-testid={`approve-case-${caseItem.id}`}
                            >
                              <PlayCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                          )}
                          {!['completed', 'stopped', 'pending_client_approval'].includes(caseItem.status) && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleStopCase(caseItem)}
                              title="Stop Verification"
                              className="text-rose-600 hover:bg-rose-50"
                              data-testid={`stop-case-${caseItem.id}`}
                            >
                              <StopCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="text-center py-12">
            <FileCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No verification cases found</p>
          </CardContent>
        </Card>
      )}

      {/* Case Details Dialog */}
      <Dialog open={!!viewCase} onOpenChange={(o) => { if (!o) { setViewCase(null); setViewEmployee(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Case Details - {viewCase?.id}
              <Badge className={STATUS_CONFIG[viewCase?.status]?.bg}>
                {STATUS_CONFIG[viewCase?.status]?.label}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {viewCase && (
            <div className="space-y-6">
              {/* Case Info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Case ID</p>
                  <p className="font-mono font-medium">{viewCase.id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Submitted Date</p>
                  <p className="font-medium">{viewCase.created_at ? new Date(viewCase.created_at).toLocaleDateString() : '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Overall Result</p>
                  <Badge className={RESULT_CONFIG[getOverallResult(viewCase, viewCase.checks)]?.bg}>
                    {RESULT_CONFIG[getOverallResult(viewCase, viewCase.checks)]?.label}
                  </Badge>
                </div>
              </div>

              {/* Employee Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Employee Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div><span className="text-slate-500">Name:</span> <span className="font-medium">{viewEmployee?.name}</span></div>
                    <div><span className="text-slate-500">Email:</span> <span className="font-medium">{viewEmployee?.email}</span></div>
                    <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{viewEmployee?.phone}</span></div>
                    <div><span className="text-slate-500">DOB:</span> <span className="font-medium">{viewEmployee?.dob || viewEmployee?.date_of_birth || '-'}</span></div>
                    <div><span className="text-slate-500">Father:</span> <span className="font-medium">{viewEmployee?.father_name || '-'}</span></div>
                    <div><span className="text-slate-500">Designation:</span> <span className="font-medium">{viewEmployee?.designation || '-'}</span></div>
                  </div>
                </CardContent>
              </Card>

              {/* Verification Status Summary */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Verification Status Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Check ID</TableHead>
                        <TableHead>Check Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Result</TableHead>
                        <TableHead>Insufficiency</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewCase.checks?.map((check, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-sm">{check.check_id}</TableCell>
                          <TableCell>{CHECK_NAMES[check.check_type] || check.check_type}</TableCell>
                          <TableCell>
                            <Badge className={
                              check.status === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                              check.status === 'failed' ? 'bg-rose-100 text-rose-700' :
                              check.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }>
                              {check.status?.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              check.result === 'clear' ? 'bg-emerald-100 text-emerald-700' :
                              check.result === 'discrepancy' ? 'bg-rose-100 text-rose-700' :
                              'bg-slate-100 text-slate-700'
                            }>
                              {check.result?.toUpperCase() || 'PENDING'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {check.has_insufficiency ? (
                              <Badge className="bg-amber-100 text-amber-700">
                                <AlertTriangle className="w-3 h-3 mr-1" />Yes
                              </Badge>
                            ) : (
                              <span className="text-emerald-600">✓ Clear</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Report Buttons */}
              <div className="flex justify-end gap-2">
                <ReportPreviewDialog 
                  caseId={viewCase.id} 
                  caseStatus={viewCase.status}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Drill-down Modal */}
      <Dialog open={showDrillDown} onOpenChange={setShowDrillDown}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{drillDownTitle}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-sm text-slate-600 mb-4">
              Showing {drillDownData.length} case(s)
            </p>
            {drillDownData.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Case ID</TableHead>
                    <TableHead className="font-semibold">Employee</TableHead>
                    <TableHead className="font-semibold">Submitted</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Progress</TableHead>
                    <TableHead className="font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillDownData.map((caseItem) => {
                    const employee = getEmployee(caseItem.employee_id);
                    const formProgress = getFormProgress(caseItem);
                    const statusConfig = STATUS_CONFIG[caseItem.status] || STATUS_CONFIG['initiated'];
                    
                    return (
                      <TableRow key={caseItem.id} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm">{caseItem.id}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{employee?.name || 'Unknown'}</p>
                            <p className="text-xs text-slate-500">{employee?.email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {caseItem.created_at ? new Date(caseItem.created_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.bg}>{statusConfig.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${formProgress.percentage === 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                                style={{ width: `${formProgress.percentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-slate-700">{formProgress.completed}/{formProgress.total}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => { setShowDrillDown(false); openViewCase(caseItem); }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-slate-500 py-8">No cases found</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Approval Confirmation Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Confirm Approval</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                You are about to approve the following case for background verification processing:
              </p>
              <div className="mt-3 space-y-1">
                <p className="font-semibold text-blue-900">{approvalCase?.employee_name}</p>
                <p className="text-sm text-blue-700">Case ID: {approvalCase?.id}</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Approval Notes (Optional)
              </label>
              <Input
                placeholder="Add any notes for this approval..."
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                data-testid="approval-notes-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowApproveDialog(false);
                  setApprovalCase(null);
                  setApprovalNotes('');
                }}
                data-testid="cancel-approval-btn"
              >
                Cancel
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleApproveCase}
                data-testid="confirm-approval-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirm Approval
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
